import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { format, isToday, isTomorrow, isPast, addDays } from 'date-fns';
import { api } from '../../lib/api';
import { useOrg } from '../../hooks/useOrg';
import { useAuth } from '../../hooks/useAuth';
import { Calendar, X, Video, Sparkles } from 'lucide-react';

const STATUS_COLORS = {
  pending: '#F59E0B',
  confirmed: '#10B981',
  completed: '#3B82F6',
  cancelled: '#F43F5E',
  no_show: '#6B7280',
  reschedule_requested: '#223368',
};

const STATUS_LABELS = {
  pending: 'Pending',
  confirmed: 'Confirmed',
  completed: 'Completed',
  cancelled: 'Cancelled',
  no_show: 'No Show',
  reschedule_requested: 'Reschedule Requested',
};

function dayLabel(date) {
  if (isToday(date)) return 'Today';
  if (isTomorrow(date)) return 'Tomorrow';
  return format(date, 'EEEE, MMMM d');
}

export default function ClientDashboard() {
  const { memberships } = useOrg();
  const { profile } = useAuth();
  const [appointments, setAppointments] = useState([]);
  // appointment_id -> {sessionId, orgId} for video visits that are live now
  const [liveVisits, setLiveVisits] = useState({});
  // Live visits with no appointment behind them (started ad-hoc from the
  // practice's Video Visits tab) — surfaced as a banner since there's no
  // appointment row to hang a Join button on.
  const [adhocVisits, setAdhocVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [rescheduleModal, setRescheduleModal] = useState(null); // appointment object
  const [rescheduleReason, setRescheduleReason] = useState('');
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [intakeForms, setIntakeForms] = useState([]);
  const [completingForm, setCompletingForm] = useState(null);
  const [signableForms, setSignableForms] = useState([]);

  // Self-booking with a connected practice
  const [bookOpen, setBookOpen] = useState(false);
  const [bookOrgId, setBookOrgId] = useState(null);
  const [bookTypes, setBookTypes] = useState([]);
  const [bookType, setBookType] = useState(null);
  const [bookProviders, setBookProviders] = useState([]);
  const [bookProviderId, setBookProviderId] = useState(null);
  const [bookDate, setBookDate] = useState('');
  const [bookSlots, setBookSlots] = useState([]);
  const [bookSlotsLoading, setBookSlotsLoading] = useState(false);
  const [bookSlot, setBookSlot] = useState(null);
  const [bookSubmitting, setBookSubmitting] = useState(false);
  const [bookError, setBookError] = useState('');
  // Referrals the clinic has accepted — the patient picks the first-visit
  // time here, from the clinic's open slots, and it books on the spot.
  const [myReferrals, setMyReferrals] = useState([]);
  const [bookReferral, setBookReferral] = useState(null);
  const [bookOffice, setBookOffice] = useState(null); // the referral's office (name/address)
  const [params, setParams] = useSearchParams();

  const medPractices = (memberships || []).filter(m => m.organization?.type === 'medical_practice');

  const openBook = (orgId, referral = null) => {
    setBookReferral(referral);
    setBookOrgId(referral?.provider_org_id || orgId || medPractices[0]?.org_id || null);
    setBookType(null); setBookDate(''); setBookSlots([]); setBookSlot(null); setBookError('');
    setBookOpen(true);
  };

  const fetchReferrals = useCallback(async () => {
    const orgId = memberships?.[0]?.org_id;
    if (!orgId) { setMyReferrals([]); return; }
    try { setMyReferrals((await api.listReferrals(orgId)) || []); }
    catch { setMyReferrals([]); } // referrals not enabled here — nothing to show
  }, [memberships]);
  useEffect(() => { fetchReferrals(); }, [fetchReferrals]);

  // Arriving from the "choose your time" email: open the booking right away.
  useEffect(() => {
    const id = params.get('referral');
    if (!id || bookOpen) return;
    const r = myReferrals.find((x) => x.id === id && x.status === 'accepted');
    if (r) { openBook(null, r); setParams({}, { replace: true }); }
  }, [myReferrals, params]);

  // Load the chosen practice's appointment types + providers
  useEffect(() => {
    if (!bookOpen || !bookOrgId) return;
    setBookTypes([]); setBookProviders([]); setBookProviderId(null); setBookType(null);
    api.getAppointmentTypes(bookOrgId)
      .then(rows => {
        const types = (rows || []).filter(t => t.is_active !== false);
        setBookTypes(types);
        if (bookReferral) {
          const hint = { chiro: /chiro/i, imaging: /mri|imag|scan|x-ray/i, ortho: /ortho/i, pt: /physical|therap|rehab/i, neuro: /neuro|concuss/i }[bookReferral.specialty];
          setBookType(types.find(t => hint && hint.test(`${t.label} ${t.key}`)) || types.find(t => /consult|new patient|eval/i.test(t.label)) || types[0] || null);
        }
      })
      .catch(() => setBookTypes([]));
    // A referral books at the office it was sent to: its providers, its hours.
    if (bookReferral) {
      api.getReferralBookingOptions(bookOrgId, bookReferral.id)
        .then(o => { setBookOffice(o.clinic?.location || null); setBookProviders(o.providers || []); setBookProviderId(o.providers?.[0]?.id || null); })
        .catch(() => setBookProviders([]));
    } else {
      setBookOffice(null);
      api.getOrgProviders(bookOrgId)
        .then(rows => { setBookProviders(rows || []); setBookProviderId(rows?.[0]?.id || null); })
        .catch(() => setBookProviders([]));
    }
  }, [bookOpen, bookOrgId, bookReferral]);

  // Load open slots when provider + date chosen
  useEffect(() => {
    if (!bookOpen || !bookOrgId || !bookProviderId || !bookDate) { setBookSlots([]); return; }
    setBookSlotsLoading(true);
    setBookSlot(null);
    const params = { provider_id: bookProviderId, date: bookDate, duration: 30, tz_offset: new Date().getTimezoneOffset() };
    (bookReferral ? api.getReferralAvailability(bookOrgId, bookReferral.id, params) : api.getAvailability(bookOrgId, params))
      .then(data => setBookSlots(data || []))
      .catch(() => setBookSlots([]))
      .finally(() => setBookSlotsLoading(false));
  }, [bookOpen, bookOrgId, bookProviderId, bookDate, bookReferral]);

  const handleBook = async () => {
    if (!bookOrgId || !bookType || !bookSlot) return;
    setBookSubmitting(true); setBookError('');
    try {
      const payload = {
        type: bookType.key,
        title: bookType.label,
        provider_id: bookProviderId,
        starts_at: bookSlot.starts_at,
        ends_at: bookSlot.ends_at,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York',
      };
      if (bookReferral) await api.bookReferralVisit(bookOrgId, bookReferral.id, payload);
      else await api.createAppointment(bookOrgId, payload);
      setBookOpen(false);
      setBookReferral(null);
      fetchAppointments();
      fetchReferrals();
    } catch (err) {
      setBookError(err.message || 'Could not request the appointment');
    } finally {
      setBookSubmitting(false);
    }
  };

  const fetchAppointments = useCallback(async () => {
    if (!memberships?.length) { setAppointments([]); setLoading(false); return; }
    try {
      const results = await Promise.all(
        memberships.map(m =>
          api.getAppointments(m.org_id, { include_collaborator: 'false' })
            .then(rows => (rows || []).map(r => ({ ...r, _orgName: m.organization?.name })))
            .catch(() => [])
        )
      );
      setAppointments(results.flat());
    } catch (err) {
      console.error('Failed to fetch appointments:', err);
    } finally {
      setLoading(false);
    }
  }, [memberships]);

  // Fetch intake forms + signable forms across every practice
  useEffect(() => {
    if (!memberships?.length) { setIntakeForms([]); setSignableForms([]); return; }
    Promise.all(memberships.map(m =>
      api.getMyIntakeForms(m.org_id)
        .then(rows => (rows || []).map(r => ({ ...r, _orgId: m.org_id, _orgName: m.organization?.name })))
        .catch(() => [])
    )).then(results => setIntakeForms(results.flat()));

    Promise.all(memberships.map(m =>
      api.getMyForms(m.org_id)
        .then(rows => (rows || []).map(r => ({ ...r, _orgId: m.org_id, _orgName: m.organization?.name })))
        .catch(() => [])
    )).then(results => setSignableForms(results.flat()));
  }, [memberships]);

  const handleCompleteForm = async (form) => {
    setCompletingForm(form.id);
    try {
      await api.completeIntakeForm(form._orgId, form.id);
      setIntakeForms(prev => prev.map(f => f.id === form.id ? { ...f, status: 'completed', completed_at: new Date().toISOString() } : f));
    } catch (err) {
      console.error(err);
    } finally {
      setCompletingForm(null);
    }
  };

  useEffect(() => { fetchAppointments(); }, [fetchAppointments]);

  // Live telehealth sessions: fetched with a slow poll so the Join button
  // shows up when the provider opens the visit, without the patient needing
  // to refresh or dig out the emailed link.
  useEffect(() => {
    if (!memberships?.length) { setLiveVisits({}); return undefined; }
    let stop = false;
    const load = () => Promise.all(memberships.map(m =>
      api.getActiveTelehealthSessions(m.org_id)
        .then(rows => (rows || []).map(r => ({ ...r, _orgId: m.org_id })))
        .catch(() => [])
    )).then(results => {
      if (stop) return;
      const map = {};
      const adhoc = [];
      results.flat().forEach(sess => {
        if (sess.appointment_id) map[sess.appointment_id] = { sessionId: sess.id, orgId: sess._orgId };
        else adhoc.push({ sessionId: sess.id, orgId: sess._orgId });
      });
      setLiveVisits(map);
      setAdhocVisits(adhoc);
    });
    load();
    const t = setInterval(load, 30000);
    return () => { stop = true; clearInterval(t); };
  }, [memberships]);

  const handleConfirm = async (appt) => {
    setActionLoading(appt.id);
    try {
      await api.updateAppointment(appt.org_id, appt.id, { status: 'confirmed' });
      setAppointments(prev => prev.map(a => a.id === appt.id ? { ...a, status: 'confirmed' } : a));
    } catch (err) {
      console.error('Failed to confirm:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancel = async (appt) => {
    if (!window.confirm('Are you sure you want to cancel this appointment?')) return;
    setActionLoading(appt.id);
    try {
      await api.updateAppointment(appt.org_id, appt.id, { status: 'cancelled' });
      setAppointments(prev => prev.map(a => a.id === appt.id ? { ...a, status: 'cancelled' } : a));
    } catch (err) {
      console.error('Failed to cancel:', err);
    } finally {
      setActionLoading(null);
    }
  };

  // Fetch available slots when reschedule date changes
  useEffect(() => {
    if (!rescheduleModal || !rescheduleDate) { setSlots([]); return; }
    setSlotsLoading(true);
    setSelectedSlot(null);
    api.getAvailability(rescheduleModal.org_id, {
      provider_id: rescheduleModal.provider_id,
      date: rescheduleDate,
      duration: 30,
      exclude_appointment_id: rescheduleModal.id,
    })
      .then(data => setSlots(data))
      .catch(() => setSlots([]))
      .finally(() => setSlotsLoading(false));
  }, [rescheduleDate, rescheduleModal]);

  const openReschedule = (appt) => {
    setRescheduleModal(appt);
    setRescheduleDate('');
    setRescheduleReason('');
    setSelectedSlot(null);
    setSlots([]);
  };

  const handleReschedule = async () => {
    if (!rescheduleModal || !selectedSlot) return;
    setActionLoading(rescheduleModal.id);
    try {
      await api.updateAppointment(rescheduleModal.org_id, rescheduleModal.id, {
        status: 'reschedule_requested',
        reschedule_reason: rescheduleReason,
        starts_at: selectedSlot.starts_at,
        ends_at: selectedSlot.ends_at,
      });
      setAppointments(prev => prev.map(a => a.id === rescheduleModal.id ? { ...a, status: 'reschedule_requested', starts_at: selectedSlot.starts_at, ends_at: selectedSlot.ends_at } : a));
      setRescheduleModal(null);
    } catch (err) {
      console.error('Failed to request reschedule:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const upcoming = appointments
    .filter(a => !isPast(new Date(a.starts_at)) || isToday(new Date(a.starts_at)))
    .filter(a => a.status !== 'cancelled')
    .sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at));

  const past = appointments
    .filter(a => isPast(new Date(a.starts_at)) && !isToday(new Date(a.starts_at)))
    .sort((a, b) => new Date(b.starts_at) - new Date(a.starts_at))
    .slice(0, 10);

  const nextAppt = upcoming[0];

  const inputClass = 'w-full px-3.5 py-[11px] bg-tertiary border border-strong rounded-[10px] text-primary-c text-[13px] font-[inherit] transition-all focus:outline-none focus:border-[#0DA7CA] focus:shadow-[0_0_0_3px_rgba(13, 167, 202,0.15)]';

  if (loading) {
    return (
      <div className="flex-1 bg-primary">
        <div className="max-w-[700px] mx-auto px-4 md:px-7 py-8">
          <div className="h-6 w-48 bg-tertiary rounded animate-pulse mb-6" />
          <div className="h-[160px] bg-tertiary rounded-xl animate-pulse mb-8" />
          <div className="space-y-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-20 bg-tertiary rounded-xl animate-pulse" style={{ animationDelay: `${i * 80}ms` }} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  const AppointmentActions = ({ appt }) => {
    const isActionable = appt.status === 'pending' || appt.status === 'confirmed';
    if (!isActionable) return null;

    return (
      <div className="flex gap-1.5 mt-2">
        {appt.status === 'pending' && (
          <button
            onClick={() => handleConfirm(appt)}
            disabled={actionLoading === appt.id}
            className="px-3 py-[5px] rounded-lg text-[11px] font-semibold text-white bg-[#10B981] hover:bg-[#059669] transition-all disabled:opacity-50"
          >
            {actionLoading === appt.id ? '...' : 'Confirm'}
          </button>
        )}
        <button
          onClick={() => openReschedule(appt)}
          disabled={actionLoading === appt.id}
          className="px-3 py-[5px] rounded-lg text-[11px] font-semibold text-[#223368] bg-[rgba(34, 51, 104,0.1)] hover:bg-[rgba(34, 51, 104,0.2)] transition-all disabled:opacity-50"
        >
          Reschedule
        </button>
        <button
          onClick={() => handleCancel(appt)}
          disabled={actionLoading === appt.id}
          className="px-3 py-[5px] rounded-lg text-[11px] font-semibold text-danger bg-danger-soft hover:bg-[rgba(244,63,94,0.2)] transition-all disabled:opacity-50"
        >
          Cancel
        </button>
      </div>
    );
  };

  return (
    <div className="flex-1 bg-primary">
      <div className="max-w-[700px] mx-auto px-4 md:px-7 py-8">
        {/* Greeting */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-7">
          <div>
            <h1 className="text-[22px] font-bold text-primary-c mb-1">
              Hello{profile?.first_name ? `, ${profile.first_name}` : ''}
            </h1>
            <p className="text-[13px] text-muted-c">
              {upcoming.length > 0
                ? `You have ${upcoming.length} upcoming appointment${upcoming.length !== 1 ? 's' : ''}`
                : 'No upcoming appointments'}
            </p>
          </div>
          {medPractices.length > 0 && (
            <button
              onClick={() => openBook()}
              className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-[10px] text-[13px] font-semibold text-white gradient-accent shadow-glow hover:-translate-y-0.5 transition-all flex-shrink-0"
            >
              <Calendar size={15} strokeWidth={2.2} />
              Book appointment
            </button>
          )}
        </div>

        {/* Accepted referrals waiting on the patient to pick a time */}
        {myReferrals.filter((r) => r.status === 'accepted').map((r) => (
          <div key={r.id} className="flex items-center gap-4 p-4 bg-elevated rounded-xl border border-theme mb-7">
            <div className="w-10 h-10 rounded-[10px] gradient-accent text-white flex items-center justify-center shrink-0"><Sparkles size={16} /></div>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-semibold text-primary-c">{r.provider?.name || 'Your clinic'} accepted your referral — choose your first visit</div>
              <div className="text-[11px] text-muted-c mt-0.5">{r.service ? `${r.service} · ` : ''}Your records already went with it. Pick a time from their open slots and it's booked on the spot.</div>
            </div>
            <button onClick={() => openBook(null, r)} className="px-4 py-2 rounded-[10px] text-[12px] font-semibold text-white gradient-accent shadow-glow shrink-0 whitespace-nowrap">Choose a time</button>
          </div>
        ))}

        {/* Live ad-hoc video visits — the provider is in the room waiting */}
        {adhocVisits.map((v) => {
          const org = memberships?.find((m) => m.org_id === v.orgId);
          return (
            <div key={v.sessionId} className="flex items-center gap-4 p-4 bg-elevated rounded-xl border border-theme mb-7">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-semibold text-primary-c">
                  {org?.organization?.name || 'Your provider'} started a video visit with you
                </div>
                <div className="text-[11px] text-muted-c mt-0.5">They're in the room now — join when you're ready.</div>
              </div>
              <Link
                to={`/telehealth/${v.sessionId}`}
                className="shrink-0 flex items-center gap-1.5 px-4 py-[8px] rounded-lg text-[12px] font-semibold text-white gradient-accent shadow-glow hover:-translate-y-0.5 transition-all"
              >
                <Video size={13} /> Join video visit
              </Link>
            </div>
          );
        })}

        {/* Intake Forms */}
        {intakeForms.length > 0 && (
          <div className="mb-7">
            {intakeForms.some(f => f.status === 'pending') && (
              <div className="bg-[rgba(245,158,11,0.08)] border border-[rgba(245,158,11,0.25)] rounded-xl p-4 mb-3">
                <div className="text-[13px] font-semibold text-primary-c">Complete Your Intake Forms</div>
                <p className="text-[12px] text-secondary-c mt-1">Please fill out the forms below before your appointment.</p>
              </div>
            )}
            <div className="space-y-2">
              {intakeForms.map(f => (
                <div key={f.id} className={`flex flex-col sm:flex-row sm:items-center gap-3 p-3.5 rounded-xl border transition-all ${
                  f.status === 'completed' ? 'bg-tertiary border-theme opacity-70' : 'bg-elevated border-theme'
                }`}>
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className={`w-9 h-9 rounded-[10px] flex items-center justify-center flex-shrink-0 ${
                      f.status === 'completed' ? 'bg-success-soft' : 'bg-accent-glow'
                    }`}>
                      {f.status === 'completed' ? (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                      ) : (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0DA7CA" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-semibold text-primary-c">{f.name}</div>
                      <div className="text-[11px] text-muted-c mt-0.5">
                        {f.status === 'completed' ? 'Completed' : 'Pending'}
                      </div>
                    </div>
                  </div>
                  {f.status === 'pending' ? (
                    <div className="flex gap-2 flex-shrink-0">
                      <a href={f.url} target="_blank" rel="noopener noreferrer"
                        className="flex-1 sm:flex-none text-center px-3 py-2 rounded-lg text-[12px] font-semibold text-white gradient-accent shadow-glow transition-all hover:-translate-y-0.5">
                        Open Form
                      </a>
                      <button
                        onClick={() => handleCompleteForm(f)}
                        disabled={completingForm === f.id}
                        className="flex-1 sm:flex-none px-3 py-2 rounded-lg text-[12px] font-semibold text-success bg-success-soft hover:bg-[rgba(16,185,129,0.2)] transition-all disabled:opacity-50"
                      >
                        {completingForm === f.id ? '...' : 'Mark Done'}
                      </button>
                    </div>
                  ) : (
                    <span className="text-[10px] font-semibold text-success flex-shrink-0 sm:self-center">Completed</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* E-Sign Forms */}
        {signableForms.filter(f => f.status === 'pending').length > 0 && (
          <div className="mb-7">
            <div className="bg-[rgba(13, 167, 202,0.08)] border border-accent-light/20 rounded-xl p-4 mb-3">
              <div className="text-[13px] font-semibold text-primary-c">Documents Requiring Your Signature</div>
              <p className="text-[12px] text-secondary-c mt-1">Please review and sign the following forms before your appointment.</p>
            </div>
            <div className="space-y-2">
              {signableForms.filter(f => f.status === 'pending').map(f => (
                <Link
                  key={f.id}
                  to={`/client/forms/${f.id}`}
                  className="flex items-center gap-3 p-3.5 bg-elevated rounded-xl border border-theme hover:bg-hover-surface transition-all"
                >
                  <div className="w-9 h-9 rounded-[10px] bg-accent-glow flex items-center justify-center flex-shrink-0">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0DA7CA" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-semibold text-primary-c">{f.template?.name || 'Form'}</div>
                    <div className="text-[11px] text-muted-c">
                      {f.appointment_date ? `Appointment: ${format(new Date(f.appointment_date), 'MMM d, yyyy')}` : 'Pending signature'}
                    </div>
                  </div>
                  <span className="text-[11px] font-semibold text-accent-light flex-shrink-0">Sign &rarr;</span>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Signed forms */}
        {signableForms.filter(f => f.status === 'signed').length > 0 && (
          <div className="mb-7">
            <h2 className="text-[10px] font-bold uppercase tracking-[1.2px] text-muted-c mb-3">Signed Documents</h2>
            <div className="space-y-1.5">
              {signableForms.filter(f => f.status === 'signed').map(f => (
                <div key={f.id} className="flex items-center gap-3 p-3 bg-tertiary rounded-xl border border-theme opacity-70">
                  <div className="w-8 h-8 rounded-[8px] bg-success-soft flex items-center justify-center flex-shrink-0">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[12px] font-semibold text-primary-c">{f.template?.name || 'Form'}</div>
                    <div className="text-[10px] text-muted-c">Signed {f.signed_at ? format(new Date(f.signed_at), 'MMM d, yyyy') : ''}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Next appointment highlight */}
        {nextAppt && (
          <div className="bg-gradient-to-br from-[rgba(13, 167, 202,0.08)] to-[rgba(34, 51, 104,0.04)] border border-accent-light/20 rounded-2xl p-6 mb-8">
            <div className="text-[10px] font-bold uppercase tracking-[1.2px] text-accent-light mb-3">Next Appointment</div>
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-[18px] font-bold text-primary-c">
                  {dayLabel(new Date(nextAppt.starts_at))}
                </div>
                <div className="text-[15px] font-semibold text-accent-light mt-1">
                  {format(new Date(nextAppt.starts_at), 'h:mm a')} — {format(new Date(nextAppt.ends_at), 'h:mm a')}
                </div>
                {(nextAppt.practice || nextAppt.provider) && (
                  <div className="text-[13px] text-secondary-c mt-2">
                    at {nextAppt.practice?.name || `${nextAppt.provider?.first_name} ${nextAppt.provider?.last_name}`}
                  </div>
                )}
                {nextAppt.location_name && (
                  <div className="text-[12px] text-muted-c mt-1">{nextAppt.location_name}</div>
                )}
                <AppointmentActions appt={nextAppt} />
              </div>
              <span
                className="text-[11px] font-semibold px-3 py-1 rounded-full text-white flex-shrink-0"
                style={{ background: STATUS_COLORS[nextAppt.status] || '#6B7280' }}
              >
                {STATUS_LABELS[nextAppt.status] || nextAppt.status}
              </span>
            </div>
          </div>
        )}

        {/* Upcoming */}
        {upcoming.length > 0 && (
          <div className="mb-8">
            <h2 className="text-[10px] font-bold uppercase tracking-[1.2px] text-muted-c mb-3">Upcoming</h2>
            <div className="space-y-2">
              {upcoming.map(appt => (
                <div key={appt.id} className="p-4 bg-elevated rounded-xl border border-theme hover:bg-hover-surface transition-all">
                  <div className="flex items-center gap-4">
                    <div className="w-[46px] text-center flex-shrink-0">
                      <div className="text-[18px] font-bold text-primary-c leading-none">{format(new Date(appt.starts_at), 'd')}</div>
                      <div className="text-[10px] font-semibold text-muted-c uppercase">{format(new Date(appt.starts_at), 'MMM')}</div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-semibold text-primary-c">
                        {format(new Date(appt.starts_at), 'h:mm a')} — {format(new Date(appt.ends_at), 'h:mm a')}
                      </div>
                      <div className="text-[12px] text-secondary-c mt-0.5">
                        {appt.practice?.name || (appt.provider ? `${appt.provider.first_name} ${appt.provider.last_name}` : 'Provider TBD')}
                        {appt.location_name && <span className="text-muted-c"> &bull; {appt.location_name}</span>}
                      </div>
                    </div>
                    <span
                      className="text-[10px] font-semibold px-2.5 py-1 rounded-full text-white flex-shrink-0"
                      style={{ background: STATUS_COLORS[appt.status] || '#6B7280' }}
                    >
                      {STATUS_LABELS[appt.status] || appt.status}
                    </span>
                  </div>
                  {liveVisits[appt.id] && (
                    <div className="ml-[62px] mt-3">
                      <Link
                        to={`/telehealth/${liveVisits[appt.id].sessionId}`}
                        className="inline-flex items-center gap-2 px-4 py-[9px] rounded-lg text-[12px] font-semibold text-white gradient-accent shadow-glow transition-all hover:-translate-y-0.5"
                      >
                        <Video size={14} /> Join video visit
                      </Link>
                    </div>
                  )}
                  <div className="ml-[62px]">
                    <AppointmentActions appt={appt} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Past */}
        {past.length > 0 && (
          <div>
            <h2 className="text-[10px] font-bold uppercase tracking-[1.2px] text-muted-c mb-3">Past Appointments</h2>
            <div className="space-y-2">
              {past.map(appt => (
                <div key={appt.id} className="flex items-center gap-4 p-4 bg-tertiary rounded-xl border border-theme opacity-70">
                  <div className="w-[46px] text-center flex-shrink-0">
                    <div className="text-[16px] font-bold text-secondary-c leading-none">{format(new Date(appt.starts_at), 'd')}</div>
                    <div className="text-[10px] font-semibold text-muted-c uppercase">{format(new Date(appt.starts_at), 'MMM')}</div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[12px] font-medium text-secondary-c">
                      {format(new Date(appt.starts_at), 'h:mm a')} &bull; {appt.practice?.name || (appt.provider ? `${appt.provider.first_name} ${appt.provider.last_name}` : 'Provider')}
                    </div>
                  </div>
                  <span
                    className="text-[10px] font-semibold px-2.5 py-1 rounded-full text-white flex-shrink-0"
                    style={{ background: STATUS_COLORS[appt.status] || '#6B7280' }}
                  >
                    {STATUS_LABELS[appt.status] || appt.status}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty state */}
        {appointments.length === 0 && (
          <div className="text-center py-16">
            <Calendar size={40} strokeWidth={1.5} className="mx-auto mb-4 text-muted-c opacity-30" />
            <h2 className="text-[16px] font-bold text-primary-c mb-2">No appointments yet</h2>
            {medPractices.length > 0 ? (
              <>
                <p className="text-[13px] text-muted-c mb-5">Request a time with your practice — they'll confirm it right away.</p>
                <button
                  onClick={() => openBook()}
                  className="px-5 py-2.5 rounded-[10px] text-[13px] font-semibold text-white gradient-accent shadow-glow hover:-translate-y-0.5 transition-all"
                >
                  Book your first appointment
                </button>
              </>
            ) : (
              <p className="text-[13px] text-muted-c">Your provider will schedule appointments for you.<br />You'll receive an email notification when they do.</p>
            )}
          </div>
        )}

        {/* Practices the patient has joined via bookings */}
        {memberships?.length > 0 && (
          <div className="mt-10 pt-8 border-t border-theme">
            <h2 className="text-[10px] font-bold uppercase tracking-[1.2px] text-muted-c mb-3">Your Practices</h2>
            <div className="space-y-2">
              {memberships.map(m => {
                const o = m.organization;
                if (!o) return null;
                return (
                  <div key={m.org_id} className="flex items-center gap-3.5 p-4 bg-elevated rounded-xl border border-theme">
                    {o.logo_url ? (
                      <img src={o.logo_url} alt="" className="w-11 h-11 rounded-[10px] object-cover flex-shrink-0" />
                    ) : (
                      <div className="w-11 h-11 rounded-[10px] bg-accent flex items-center justify-center text-[13px] font-bold text-white flex-shrink-0">
                        {o.name?.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="text-[14px] font-semibold text-primary-c truncate">{o.name}</div>
                      <div className="text-[12px] text-muted-c truncate">
                        {[o.phone, o.email].filter(Boolean).join(' \u2022 ')}
                      </div>
                    </div>
                    {o.type === 'medical_practice' && (
                      <button
                        onClick={() => openBook(m.org_id)}
                        className="px-4 py-2 rounded-[10px] text-[12px] font-semibold text-white gradient-accent shadow-glow hover:-translate-y-0.5 transition-all flex-shrink-0"
                      >
                        Book
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Book appointment modal */}
      {bookOpen && (() => {
        const minDate = format(addDays(new Date(), 1), 'yyyy-MM-dd');
        const openSlots = bookSlots.filter(s => s.available);
        const bookOrg = medPractices.find(m => m.org_id === bookOrgId)?.organization;
        return (
          <div className="fixed inset-0 bg-overlay backdrop-blur-lg flex items-center justify-center z-[200] animate-fade-in p-4" onClick={() => { setBookOpen(false); setBookReferral(null); }}>
            <div className="w-full max-w-[480px] max-h-[90vh] bg-elevated border border-theme rounded-xl shadow-2xl animate-modal-in overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between px-5 sm:px-6 py-4 sm:py-5 border-b border-theme flex-shrink-0">
                <h3 className="text-[17px] sm:text-[18px] font-bold tracking-tight text-primary-c">{bookReferral ? 'Choose your first visit' : 'Book an Appointment'}</h3>
                <button onClick={() => setBookOpen(false)} className="w-9 h-9 rounded-lg bg-tertiary text-muted-c flex items-center justify-center hover:bg-hover-surface hover:text-primary-c transition-all">
                  <X size={14} strokeWidth={2.2} />
                </button>
              </div>
              <div className="px-5 sm:px-6 py-5 sm:py-6 space-y-5 overflow-y-auto">
                {/* Practice */}
                {bookReferral ? (
                  <div className="bg-tertiary rounded-[10px] p-3.5 border border-theme">
                    <div className="text-[10px] font-bold uppercase tracking-[1px] text-muted-c mb-1">Clinic</div>
                    <div className="text-[13px] font-semibold text-primary-c">{bookReferral.provider?.name}</div>
                    {bookOffice && <div className="text-[11px] text-muted-c mt-0.5">{[bookOffice.name, bookOffice.address_line1, bookOffice.city, bookOffice.state].filter(Boolean).join(', ')}</div>}
                    <div className="text-[11px] text-muted-c mt-0.5">Accepted your referral{bookReferral.firm?.name ? ` from ${bookReferral.firm.name}` : ''}{bookReferral.service ? ` · ${bookReferral.service}` : ''}</div>
                  </div>
                ) : medPractices.length > 1 ? (
                  <div>
                    <label className="block text-[12px] font-semibold text-secondary-c mb-2">Practice</label>
                    <select className={inputClass} value={bookOrgId || ''} onChange={e => setBookOrgId(e.target.value)}>
                      {medPractices.map(m => (
                        <option key={m.org_id} value={m.org_id}>{m.organization?.name}</option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <div className="bg-tertiary rounded-[10px] p-3.5 border border-theme">
                    <div className="text-[10px] font-bold uppercase tracking-[1px] text-muted-c mb-1">Practice</div>
                    <div className="text-[13px] font-semibold text-primary-c">{bookOrg?.name}</div>
                  </div>
                )}

                {/* Visit type */}
                <div>
                  <label className="block text-[12px] font-semibold text-secondary-c mb-2">What type of visit?</label>
                  {bookTypes.length === 0 ? (
                    <p className="text-[12px] text-muted-c py-3 text-center bg-tertiary rounded-[10px]">Loading visit types…</p>
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {bookTypes.map(t => {
                        const isSel = bookType?.key === t.key;
                        return (
                          <button
                            key={t.key}
                            onClick={() => setBookType(t)}
                            className={`px-3.5 py-2 rounded-lg text-[12px] font-semibold transition-all border ${
                              isSel ? 'text-white border-transparent shadow-glow' : 'bg-tertiary text-secondary-c border-strong hover:bg-hover-surface hover:text-primary-c'
                            }`}
                            style={isSel ? { background: t.color || '#0DA7CA' } : undefined}
                          >
                            {t.label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Provider (only when there's a choice) */}
                {bookProviders.length > 1 && (
                  <div>
                    <label className="block text-[12px] font-semibold text-secondary-c mb-2">Provider</label>
                    <select className={inputClass} value={bookProviderId || ''} onChange={e => setBookProviderId(e.target.value)}>
                      {bookProviders.map(p => (
                        <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Date */}
                <div>
                  <label className="block text-[12px] font-semibold text-secondary-c mb-2">Pick a date</label>
                  <input type="date" className={inputClass} min={minDate} value={bookDate} onChange={e => setBookDate(e.target.value)} />
                </div>

                {/* Slots */}
                {bookDate && (
                  <div>
                    <label className="block text-[12px] font-semibold text-secondary-c mb-2">Available times</label>
                    {bookSlotsLoading ? (
                      <div className="grid grid-cols-4 gap-1.5">
                        {Array.from({ length: 8 }).map((_, i) => (
                          <div key={i} className="h-9 bg-tertiary rounded-lg animate-pulse" style={{ animationDelay: `${i * 40}ms` }} />
                        ))}
                      </div>
                    ) : openSlots.length === 0 ? (
                      <p className="text-[12px] text-muted-c py-4 text-center bg-tertiary rounded-[10px]">No available times on this date</p>
                    ) : (
                      <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5">
                        {openSlots.map(slot => {
                          const isSel = bookSlot?.starts_at === slot.starts_at;
                          return (
                            <button
                              key={slot.starts_at}
                              onClick={() => setBookSlot(slot)}
                              className={`py-2.5 rounded-lg text-[12px] font-semibold transition-all border ${
                                isSel ? 'text-white border-transparent shadow-glow' : 'bg-tertiary text-secondary-c border-strong hover:bg-hover-surface hover:text-primary-c'
                              }`}
                              style={isSel ? { background: '#0DA7CA' } : undefined}
                            >
                              {format(new Date(slot.starts_at), 'h:mm a')}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {bookError && (
                  <div className="px-4 py-2.5 rounded-[10px] bg-danger-soft text-danger text-[12px] font-medium">{bookError}</div>
                )}

                {/* Actions */}
                <div className="flex items-center justify-between gap-2.5 pt-4 border-t border-theme">
                  <p className="text-[11px] text-muted-c">{bookReferral ? 'Booked on the spot — the clinic already accepted you. You\'ll get a confirmation by email and text.' : 'The practice confirms your request — you\'ll get an email and text.'}</p>
                  <button
                    onClick={handleBook}
                    disabled={!bookType || !bookSlot || bookSubmitting}
                    className="px-5 py-2.5 rounded-[10px] text-[13px] font-semibold text-white gradient-accent shadow-glow-lg hover:-translate-y-0.5 transition-all disabled:opacity-50 flex-shrink-0"
                  >
                    {bookSubmitting ? (bookReferral ? 'Booking…' : 'Requesting…') : (bookReferral ? 'Book this time' : 'Request Appointment')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Reschedule modal */}
      {rescheduleModal && (() => {
        const minDate = format(addDays(new Date(), 1), 'yyyy-MM-dd');
        const availableSlots = slots.filter(s => s.available);
        return (
          <div className="fixed inset-0 bg-overlay backdrop-blur-lg flex items-center justify-center z-[200] animate-fade-in p-4" onClick={() => setRescheduleModal(null)}>
            <div className="w-full max-w-[480px] max-h-[90vh] bg-elevated border border-theme rounded-xl shadow-2xl animate-modal-in overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between px-5 sm:px-6 py-4 sm:py-5 border-b border-theme flex-shrink-0">
                <h3 className="text-[17px] sm:text-[18px] font-bold tracking-tight text-primary-c">Reschedule Appointment</h3>
                <button onClick={() => setRescheduleModal(null)} className="w-9 h-9 rounded-lg bg-tertiary text-muted-c flex items-center justify-center hover:bg-hover-surface hover:text-primary-c transition-all">
                  <X size={14} strokeWidth={2.2} />
                </button>
              </div>
              <div className="px-5 sm:px-6 py-5 sm:py-6 space-y-5 overflow-y-auto">
                {/* Current appointment */}
                <div className="bg-tertiary rounded-[10px] p-3.5 border border-theme">
                  <div className="text-[10px] font-bold uppercase tracking-[1px] text-muted-c mb-1.5">Current</div>
                  <div className="text-[13px] font-semibold text-primary-c">
                    {format(new Date(rescheduleModal.starts_at), 'EEEE, MMMM d')} &bull; {format(new Date(rescheduleModal.starts_at), 'h:mm a')}
                  </div>
                  {rescheduleModal.provider && (
                    <div className="text-[11px] text-muted-c mt-0.5">at {rescheduleModal.practice?.name || (rescheduleModal.provider ? `${rescheduleModal.provider.first_name} ${rescheduleModal.provider.last_name}` : '')}</div>
                  )}
                </div>

                {/* Pick a new date */}
                <div>
                  <label className="block text-[12px] font-semibold text-secondary-c mb-2">Pick a new date</label>
                  <input
                    type="date"
                    className={inputClass}
                    min={minDate}
                    value={rescheduleDate}
                    onChange={e => setRescheduleDate(e.target.value)}
                  />
                </div>

                {/* Available slots */}
                {rescheduleDate && (
                  <div>
                    <label className="block text-[12px] font-semibold text-secondary-c mb-2">Available times</label>
                    {slotsLoading ? (
                      <div className="grid grid-cols-4 gap-1.5">
                        {Array.from({ length: 8 }).map((_, i) => (
                          <div key={i} className="h-9 bg-tertiary rounded-lg animate-pulse" style={{ animationDelay: `${i * 40}ms` }} />
                        ))}
                      </div>
                    ) : availableSlots.length === 0 ? (
                      <p className="text-[12px] text-muted-c py-4 text-center bg-tertiary rounded-[10px]">No available times on this date</p>
                    ) : (
                      <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5">
                        {availableSlots.map(slot => {
                          const isSelected = selectedSlot?.starts_at === slot.starts_at;
                          return (
                            <button
                              key={slot.starts_at}
                              onClick={() => setSelectedSlot(slot)}
                              className={`py-2.5 rounded-lg text-[12px] font-semibold transition-all border ${
                                isSelected
                                  ? 'text-white border-transparent shadow-glow'
                                  : 'bg-tertiary text-secondary-c border-strong hover:bg-hover-surface hover:text-primary-c'
                              }`}
                              style={isSelected ? { background: '#0DA7CA' } : undefined}
                            >
                              {format(new Date(slot.starts_at), 'h:mm a')}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* Reason */}
                <div>
                  <label className="block text-[12px] font-semibold text-secondary-c mb-2">Reason (optional)</label>
                  <textarea
                    className={inputClass}
                    rows={2}
                    placeholder="e.g., I have a conflict at this time..."
                    value={rescheduleReason}
                    onChange={e => setRescheduleReason(e.target.value)}
                  />
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-2.5 pt-4 border-t border-theme">
                  <button onClick={() => setRescheduleModal(null)} className="px-5 py-2.5 rounded-[10px] text-[13px] font-semibold text-primary-c bg-tertiary border border-theme hover:bg-hover-surface transition-all">
                    Cancel
                  </button>
                  <button
                    onClick={handleReschedule}
                    disabled={!selectedSlot || actionLoading === rescheduleModal?.id}
                    className="px-5 py-2.5 rounded-[10px] text-[13px] font-semibold text-white gradient-accent shadow-glow-lg hover:-translate-y-0.5 transition-all disabled:opacity-50"
                  >
                    {actionLoading === rescheduleModal?.id ? 'Sending...' : 'Request This Time'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
